import React, { useState } from 'react';
import { Diary as DiaryType } from '../../../domain/grandma';
import { createUseStyles } from 'react-jss';
import { useDialog } from '../../../hooks/useDialog';
import { DiaryPage } from './DiaryPage';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import diaryImage from '../../../assets/images/diary.png';

interface IDiaryDialogProps {
    diary: DiaryType;
}

const useStyles = createUseStyles({
    dialog: {
        maxWidth: 1100,
        width: '80%',
        padding: 0,
        backgroundImage: `url(${diaryImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundColor: 'transparent',
        aspectRatio: '1 / 0.7',
        flexDirection: 'column',
        '&:modal': {
            display: 'flex',
        },
    },
    book: {
        display: 'grid',
        gridTemplate: '"left right" "actionLeft actionRight" min-content / 1fr 1fr',
        fontFamily: 'var(--font-grandma)',
        position: 'relative',
        height: '100%',
    },
    bookWrapper: {
        marginLeft: '10%',
        marginRight: '15%',
        marginTop: '5%',
        marginBottom: '7%',
        height: '100%',
    },
    page: {
        padding: '1.5rem 2rem',
        overflow: 'auto',
    },
    pageLeft: {
        gridArea: 'left',
    },
    pageRight: {
        gridArea: 'right',
    },
    actionLeft: {
        gridArea: 'actionLeft',
    },
    actionRight: {
        gridArea: 'actionRight',
    },
    paginationButton: {
        minHeight: 150,
        border: 'none',
        background: 'transparent',
        opacity: 0.8,
        '&:hover, &:focus': {
            fontStyle: 'italic',
            opacity: 1,
        },
    },
    closeButton: {
        height: '200px',
        width: '100px',
        border: 'none',
        background: 'transparent',
    },
    closeButtonWrapper: {
        position: 'absolute',
        top: '50%',
        right: '-17%',
    },
});

export const DiaryDialog: React.FunctionComponent<IDiaryDialogProps> = props => {
    const { t } = useTranslation();
    const styles = useStyles();
    const [ pageIndex, setPageIndex ] = useState((Math.floor((props.diary.length + 1) / 2) - 1) * 2);

    const pageL = props.diary[pageIndex] ?? null;
    const pageR = props.diary[pageIndex + 1] ?? null;

    return (
        <div className={styles.bookWrapper}>
            <div className={styles.book}>
                <div className={clsx(styles.page, styles.pageLeft)}>
                    {!!pageL && <DiaryPage page={pageL}/>}
                </div>
                <div className={clsx(styles.page, styles.pageRight)}>
                    {!!pageR && <DiaryPage page={pageR}/>}
                </div>
                {pageIndex > 0 && (
                    <button
                        className={clsx(styles.paginationButton, styles.actionLeft)}
                        onClick={() => setPageIndex(p => Math.max(0, p - 2))}
                    >
                        {t('diary.previousPage')}
                    </button>
                )}
                {pageIndex + 2 < props.diary.length && (
                    <button
                        className={clsx(styles.paginationButton, styles.actionRight)}
                        onClick={() => setPageIndex(p => p + 2)}
                    >
                        {t('diary.nextPage')}
                    </button>
                )}
                <form method="dialog" className={styles.closeButtonWrapper}>
                    <button
                        className={styles.closeButton}
                        aria-label={t('close')}
                    ></button>
                </form>
            </div>
        </div>
    );
};

export const useDiary = (diary: DiaryType) => {
    const styles = useStyles();
    return useDialog({
        children: <DiaryDialog diary={diary}/>,
        className: styles.dialog,
    });
};
